import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'
import api from '../services/api'

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref(localStorage.getItem('token') || null)
  const user = ref(JSON.parse(localStorage.getItem('user')) || null)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value)

  const userName = computed(() => user.value?.name || '')

  const userInitials = computed(() => {
    if (!user.value?.name) return ''

    const nameParts = user.value.name.split(' ')
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase()
    }

    return (
      nameParts[0].charAt(0) + 
      nameParts[nameParts.length - 1].charAt(0)
    ).toUpperCase()
  })

  // Actions
  const login = async (credentials) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.auth.login(credentials)

      if (response.data.messageCode === 200) {
        const result = response.data.result

        // Save token and user data
        token.value = result.token
        user.value = {
          id: result.id,
          name: result.name,
          username: result.username,
          email: result.email
        }

        // Save to localStorage
        localStorage.setItem('token', token.value)
        localStorage.setItem('user', JSON.stringify(user.value))

        return { success: true }
      } else {
        throw new Error(response.data.message || 'Login failed')
      }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Login failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const register = async (userData) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.auth.register(userData)

      if (response.data.messageCode === 200) {
        return { success: true }
      } else {
        throw new Error(response.data.message || 'Registration failed')
      }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Registration failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    // Clear state
    token.value = null
    user.value = null

    // Clear localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  const checkTokenExpiration = () => {
    if (!token.value) return false

    try {
      const decoded = jwtDecode(token.value)
      const currentTime = Date.now() / 1000

      if (decoded.exp < currentTime) {
        // Token expired, log out
        logout()
        return false
      }

      return true
    } catch (error) {
      // Invalid token
      logout()
      return false
    }
  }

  const updateProfile = async (profileData) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.auth.updateProfile(profileData)

      if (response.data.messageCode === 200) {
        // Update user data
        user.value = {
          ...user.value,
          name: response.data.result.name,
          email: response.data.result.email
        }

        // Update localStorage
        localStorage.setItem('user', JSON.stringify(user.value))

        return { success: true }
      } else {
        throw new Error(response.data.message || 'Update failed')
      }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Update failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.auth.changePassword(passwordData)

      if (response.data.messageCode === 200) {
        return { success: true }
      } else {
        throw new Error(response.data.message || 'Password change failed')
      }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Password change failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // Check token expiration on store initialization
  checkTokenExpiration()

  return {
    // State
    token,
    user,
    loading,
    error,

    // Getters
    isAuthenticated,
    userName,
    userInitials,

    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    checkTokenExpiration
  }
})